import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';

const YearSettings = () => {
  const [viewableYear, setViewableYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [yearResponse, availableResponse] = await Promise.all([
        settingsAPI.getViewableYear(),
        settingsAPI.getAvailableYears(),
      ]);
      
      setViewableYear(yearResponse.data.year);
      setAvailableYears(availableResponse.data.years);
      setSelectedYear(yearResponse.data.year);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Failed to load settings');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      await settingsAPI.setViewableYear(selectedYear);
      setViewableYear(selectedYear);
      setIsEditing(false);
      // Reload the page to refresh the expense list for all users
      window.location.reload();
    } catch (err) {
      setError('Failed to update viewable year');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedYear(viewableYear);
    setIsEditing(false);
    setError('');
  };

  const generateYearRangeOptions = () => {
    const currentYear = new Date().getFullYear();
    const ranges = new Set();
    
    // Extract start years from available years
    availableYears.forEach(year => {
      const startYear = typeof year === 'string' ? parseInt(year.split('-')[0]) : year;
      ranges.add(startYear);
    });
    
    // Add the last 2 years and the next 8 years
    for (let i = -2; i <= 8; i++) {
      ranges.add(currentYear + i);
    }
    
    return Array.from(ranges).sort((a, b) => b - a).map(year => `${year} - ${year + 1}`);
  };

  if (viewableYear === null) {
    return null;
  }

  return (
    <div className="year-settings">
      <div className="year-settings-content">
        <div className="year-info">
          <span className="year-label">📅 User Viewable Year:</span>
          {!isEditing ? (
            <>
              <span className="year-value">{viewableYear}</span>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => setIsEditing(true)}
              >
                Change
              </button>
            </>
          ) : (
            <>
              <select
                className="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                disabled={loading}
              >
                {generateYearRangeOptions().map(yearRange => (
                  <option key={yearRange} value={yearRange}>{yearRange}</option>
                ))}
              </select>
              <button 
                className="btn btn-sm btn-success"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
        {error && <div className="error-message-inline">{error}</div>}
      </div>
      <p className="year-help-text">
        All users, including admins, will only see expenses from the selected financial year.
      </p>
    </div>
  );
};

export default YearSettings;